import React, { useContext } from "react";
import useTranslation from "../../../hooks/useTranslation";
import { useProductManagement } from "../../../hooks/productManagement";
import FeaturePageLayout from "../../Shared/FeaturePageLayout/FeaturePageLayout";
import FeatureHeader from "../../Shared/FeaturePageLayout/FeatureHeader/FeatureHeader";
import FeatureTable from "../../Shared/FeaturePageLayout/FeatureTable/FeatureTable";
import FeaturePagination from "../../Shared/FeaturePageLayout/FeaturePagination/FeaturePagination";
import ProductsFilterOptions from "./ProductsFilterOptions/ProductsFilterOptions";
import AddProductModal from "./AddProductModal/AddProductModal";
import EditProductModal from "./EditProductModal/EditProductModal";
import ConfirmationModal from "../../Shared/ConfirmationModal/ConfirmationModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faCaretDown,
} from "../../../helpers/icons/fontAwesomeIcons";
import UserContext from "../../../context/UserContext";
import USER_ROLES from "../../../constants/userRoles";
import { PRODUCT_STATUS } from "../../../constants/productConstants";

const ProductsSection = () => {
  const { t } = useTranslation("productManagerPage");
  const { user } = useContext(UserContext);
  const isSuperAdmin = user && user.roles === USER_ROLES.SUPER_ADMIN;
  const {
    // State
    setSearchTerm,
    selectedCompany,
    setSelectedCompany,
    showAddModal,
    setShowAddModal,
    showEditModal,
    setShowEditModal,
    selectedProduct,
    showDeleteConfirmation,
    productToDelete,
    productTypeFilter,
    setProductTypeFilter,
    statusFilter,
    setStatusFilter,
    filteredProducts,
    productTypes,
    recordsPerPage,
    setRecordsPerPage,
    currentPage,
    setCurrentPage,
    loadingProducts,
    companies,
    paginatedProducts,

    // Functions
    handleAddProduct,
    handleSaveProduct,
    handleUpdateProduct,
    handleConfirmDelete,
    handleCancelDelete,
    handleActionClick,
    applyFilters,
  } = useProductManagement();

  // Create selectors array
  const selectors = isSuperAdmin
    ? [
        {
          items: companies,
          loading: false,
          selectedValue: selectedCompany,
          onChange: setSelectedCompany,
          valueField: "companyid",
          labelField: "companyname",
          placeholderKey: "company",
          icon: faCaretDown,
        },
      ]
    : [];

  // Define the columns for the products table
  const productColumns = [
    {
      key: "baseid",
      label: t("baseid"),
      render: (row) => renderWithDefault(row.baseid),
    },
    {
      key: "probeid", // Changed from "prodeid" to "probeid" to match data
      label: t("probeid"),
      render: (row) => renderWithDefault(row.probeid),
    },
    {
      key: "regnumber",
      label: t("regnumber"),
      render: (row) => renderWithDefault(row.regnumber),
    },
    {
      key: "probelength",
      label: t("probelength"),
      render: (row) => renderWithDefault(row.probelength),
    },
    {
      key: "probesensnum",
      label: t("probesensnum"),
      render: (row) => renderWithDefault(row.probesensnum),
    },
    {
      key: "imeei",
      label: t("imeei"),
      render: (row) => renderWithDefault(row.imeei),
    },
    {
      key: "sim",
      label: t("sim"),
      render: (row) => renderWithDefault(row.sim),
    },
    {
      key: "comments",
      label: t("comments"),
      render: (row) => renderWithDefault(row.comments),
    },
    {
      key: "status",
      label: t("status"),
      render: (row) => {
        if (row.status === null) return "-";

        // Get translation key from constants or use raw value as fallback
        const translationKey = PRODUCT_STATUS[row.status] || row.status;

        // Create a styled status badge with translated text
        return (
          <span className={`status-badge status-${row.status.toLowerCase()}`}>
            {t(translationKey)}
          </span>
        );
      },
    },
    {
      key: "companies_id",
      label: t("company"),
      render: (row) => {
        const company = companies.find((c) => c.companyid === row.companies_id);
        return company
          ? company.companyname
          : renderWithDefault(row.companies_id);
      },
    },
    {
      key: "sim_id",
      label: t("sim_id"),
      render: (row) => renderWithDefault(row.sim_id),
    },
    {
      key: "probe_type",
      label: t("probe_type"),
      render: (row) =>
        renderWithDefault(row.probe_type ? row.probe_type.trim() : null),
    },
    {
      key: "product_types_id",
      label: t("product_type"),
      render: (row) => {
        const productType = productTypes.find(
          (pt) => pt.product_types_id === row.product_types_id
        );
        return productType
          ? productType.product_type_name
          : renderWithDefault(row.product_types_id);
      },
    },
  ];

  // Render helper function for null values
  const renderWithDefault = (value) => (value === null ? "-" : value);

  return (
    <>
      <FeaturePageLayout>
        <FeatureHeader
          t={t}
          title={t("products")}
          search={setSearchTerm}
          selectors={selectors}
          showCompanySelector={true}
          selectedCompany={selectedCompany}
          onCompanyChange={setSelectedCompany}
          selectorUniqeClassname="product-manager-page__company-selector"
          display={{
            count: recordsPerPage,
            onChange: setRecordsPerPage,
            options: [5, 10, 20, 50],
          }}
          actionButton={
            isSuperAdmin
              ? {
                  onClick: handleAddProduct,
                  icon: <FontAwesomeIcon icon={faPlus} />,
                  label: "add_product",
                }
              : null
          }
          filters={
            <ProductsFilterOptions
              t={t}
              productTypeFilter={productTypeFilter}
              setProductTypeFilter={setProductTypeFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              productTypes={productTypes}
              applyFilters={applyFilters}
            />
          }
        />
        {/* Render products table or list here */}
        <FeatureTable
          t={t}
          data={paginatedProducts}
          columns={productColumns}
          tableType="products"
          pagination={null}
          loading={loadingProducts}
          actions={{
            view: true,
            edit: true,
            delete: true,
            custom: [],
          }}
          onActionClick={handleActionClick}
        />
      </FeaturePageLayout>
      {filteredProducts.length > 0 && (
        <div className="pagination-container">
          <FeaturePagination
            t={t}
            currentPage={currentPage}
            totalPages={Math.ceil(
              (filteredProducts?.length || 0) / recordsPerPage
            )}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
      {/* Modals for adding, editing and deleting products */}
      <AddProductModal
        t={t}
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveProduct}
        productTypes={productTypes}
        tableType="products"
      />
      <EditProductModal
        t={t}
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleUpdateProduct}
        product={selectedProduct}
        productTypes={productTypes}
        tableType="products"
      />
      <ConfirmationModal
        isVisible={showDeleteConfirmation}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        message={
          productToDelete
            ? t("confirm_delete_product", {
                id: productToDelete.id,
                name: productToDelete.regnumber,
              })
            : ""
        }
      />
    </>
  );
};

export default ProductsSection;
